import { useEffect, useState } from 'react';

import { useMaTransactionsContext } from '../../../MaTransactionsContext';
import { useGetDealsPaginated } from './useGetDealsPaginated';
import { useGetTransactions } from './useGetTransactions';

const genUniqueId = prefix => {
  const randomNum = Math.floor(Math.random() * 1000000);
  const timestamp = Date.now();
  return `${prefix}-${randomNum}-${timestamp}`;
};

export const useGetTableRows = ({ paginationModel, sortModel }) => {
  const perPage = 25;
  const { filters } = useMaTransactionsContext();
  const [rows, setRows] = useState([]);
  const [pageByDeal, setPageByDeal] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [transactionsByDeal, setTransactionsByDeal] = useState({});

  const {
    deals,
    totalCount: totalDeals,
    loading,
    error,
  } = useGetDealsPaginated(
    paginationModel?.page,
    paginationModel?.pageSize,
    sortModel[0]?.sort,
    sortModel[0]?.field,
  );
  const { getTransactions, transactions, totalCount: totalTransactions } = useGetTransactions();

  const expandedDeals = deals.filter(deal => expandedRows[deal.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset transactionsByDeal, expandedRows and pageByDeal when filters change
  useEffect(() => {
    setTransactionsByDeal({});
    setExpandedRows({});
    setPageByDeal({});
  }, [filters]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Can't add transactionsByDeal to dependencies as it creates a loop
  useEffect(() => {
    let called = false;
    const updatedTransactionsByDeal = { ...transactionsByDeal };

    expandedDeals.forEach(deal => {
      const currentPage = pageByDeal[deal.id] || 0;
      if (
        updatedTransactionsByDeal[deal.id]?.loading ||
        updatedTransactionsByDeal[deal.id]?.page === currentPage
      )
        return;

      // Only fetch transactions if the deal is expanded and the page has changed
      getTransactions({
        deals: [Number(deal.id)],
        page: currentPage,
        perPage,
        sort: sortModel[0]?.sort,
        sortBy: sortModel[0]?.field,
      });

      called = true;
      updatedTransactionsByDeal[deal.id] = {
        transactions: [],
        ...updatedTransactionsByDeal[deal.id],
        page: currentPage,
        loading: true,
        fetched: false,
      };
    });

    if (called) {
      // Only update the state if we called getTransactions
      setTransactionsByDeal(updatedTransactionsByDeal);
    }
  }, [expandedDeals, getTransactions, sortModel, pageByDeal]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Can't add transactionsByDeal to dependencies as it creates a loop
  useEffect(() => {
    if (transactions?.length > 0) {
      const dealId = transactions[0]?.maCompanyTransaction?.maDeal?.id;

      if (!dealId) return;
      if (!transactionsByDeal[dealId]) return;
      if (transactionsByDeal[dealId]?.fetched) return;

      const dealTransactions = transactionsByDeal[dealId].transactions || [];

      // Add the new transactions to the existing transactions for this deal
      dealTransactions.push(
        ...transactions.map(transaction => ({
          ...transaction,
          isTransaction: true,
          rowId: genUniqueId('transaction'),
        })),
      );

      setTransactionsByDeal(prev => ({
        ...prev,
        [dealId]: {
          ...prev[dealId],
          transactions: dealTransactions,
          loadMore: totalTransactions > dealTransactions.length,
          loading: false,
          fetched: true,
        },
      }));
    }
  }, [transactions, totalTransactions]);

  // Using useEffect instead of useMemo to ensure the rows are only updated when the deals are loaded
  useEffect(() => {
    // Prevent rows from updating if deals are still loading
    if (loading) return;

    // Generate rows for deals
    const dealRows = deals.map(deal => ({
      ...deal,
      isExpanded: !!expandedRows[deal.id],
      isGroup: deal?.maAssetTransactionsCount > 1,
      isDeal: true,
      rowId: genUniqueId('deal'),
    }));

    // Merge deals and their transactions into a single rows array
    const allRows = dealRows.flatMap(deal => {
      const rows = [deal];
      if (deal.isExpanded) {
        const dealTransactions = transactionsByDeal[deal.id]?.transactions || [];
        const isLoadMore = transactionsByDeal[deal.id]?.loadMore;
        const isLoading = transactionsByDeal[deal.id]?.loading;

        if (dealTransactions.length > 0) {
          // Mark first and last transaction for styling
          dealTransactions[0].isFirst = true;
          dealTransactions[dealTransactions.length - 1].isLast = !isLoadMore && !isLoading;
          rows.push(...dealTransactions);
        }

        if (isLoadMore) {
          rows.push({
            id: `${deal.id}-load-more`,
            rowId: genUniqueId('load-more'),
            dealId: deal.id,
            isLoadMore: true,
          });
        }

        if (isLoading) {
          rows.push({
            id: `${deal.id}-loading`,
            rowId: genUniqueId('loading'),
            dealId: deal.id,
            isLoading: true,
          });
        }
      }
      return rows;
    });

    setRows(allRows);
  }, [deals, expandedRows, transactionsByDeal, loading]);

  const onRowClick = params => {
    // If the row is a load more row, load more transactions
    if (params.row.isLoadMore) {
      const dealId = params?.row?.dealId;
      const currentPage = pageByDeal[dealId] || 0;
      if (dealId) {
        setPageByDeal(prev => ({
          ...prev,
          [dealId]: currentPage + 1,
        }));
      }
    }

    if (params.row.isGroup) {
      // Toggle the expanded state of the clicked row
      setExpandedRows(prev => ({
        ...prev,
        [params.row.id]: !prev[params.row.id],
      }));
    }
  };

  return {
    rows,
    totalCount: totalDeals,
    loading, // We only show the loading state for deals
    error,
    transactionsByDeal,
    onRowClick,
  };
};
